/**
 * Session Cache Manager
 * Handles TTL-based caching of session data with intelligent invalidation
 */

import { sessionEventSystem } from "./session-event-system";

export interface SessionCacheEntry {
  session: any | null;
  timestamp: number;
  ttl: number;
  version: number;
  invalidationReason?: string;
  markedStale?: boolean;
}

export interface SessionCacheManager {
  get(): any | null;
  set(session: any): void;
  invalidate(reason?: string): void;
  isStale(): boolean;
  getLastUpdated(): Date;
  getVersion(): number;
  markStale(reason?: string): void;
  getCacheInfo(): SessionCacheEntry | null;
  getStats(): {
    hasCache: boolean;
    isStale: boolean;
    lastUpdated: Date | null;
    version: number;
    ttl: number;
    age: number;
  };
}

class SessionCacheManagerImpl implements SessionCacheManager {
  private cache: SessionCacheEntry | null = null;
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  get(): any | null {
    if (!this.cache) {
      return null;
    }

    // Check if marked as stale
    if (this.cache.markedStale) {
      this.invalidate("marked-stale");
      return null;
    }

    if (this.isStale()) {
      this.invalidate("ttl-expired");
      return null;
    }

    return this.cache.session;
  }

  set(session: any): void {
    const previousSession = this.cache?.session;
    const newVersion = (this.cache?.version ?? 0) + 1;

    this.cache = {
      session,
      timestamp: Date.now(),
      ttl: this.defaultTTL,
      version: newVersion,
      markedStale: false,
    };

    // Emit session change event if session actually changed
    if (JSON.stringify(previousSession) !== JSON.stringify(session)) {
      sessionEventSystem.emit("session-changed", {
        previousSession,
        newSession: session,
        version: newVersion,
      });
    }
  }

  invalidate(reason?: string): void {
    if (this.cache) {
      this.cache.invalidationReason = reason;
    }

    this.cache = null;

    // Emit cache invalidation event
    sessionEventSystem.emit("cache-invalidated", { reason });
  }

  isStale(): boolean {
    if (!this.cache) {
      return true;
    }

    if (this.cache.markedStale) {
      return true;
    }

    return Date.now() - this.cache.timestamp > this.cache.ttl;
  }

  getLastUpdated(): Date {
    return this.cache ? new Date(this.cache.timestamp) : new Date(0);
  }

  getVersion(): number {
    return this.cache?.version ?? 0;
  }

  markStale(reason?: string): void {
    if (this.cache) {
      this.cache.markedStale = true;
      this.cache.invalidationReason = reason;
    }
  }

  getCacheInfo(): SessionCacheEntry | null {
    return this.cache ? { ...this.cache } : null;
  }

  getStats() {
    const now = Date.now();
    return {
      hasCache: !!this.cache,
      isStale: this.isStale(),
      lastUpdated: this.cache ? new Date(this.cache.timestamp) : null,
      version: this.cache?.version ?? 0,
      ttl: this.cache?.ttl ?? this.defaultTTL,
      age: this.cache ? now - this.cache.timestamp : 0,
    };
  }
}

// Singleton instance
export const sessionCacheManager = new SessionCacheManagerImpl();
